/**
 * @fileoverview Notification Context
 * Shared notification state between Header and NotificationsPage
 */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useMarket } from './MarketContext';

export interface Notification {
  id: number;
  type: 'warning' | 'success' | 'info' | 'error';
  title: string;
  titlePtBR?: string;
  message: string;
  messagePtBR?: string;
  time: string;
  timePtBR?: string;
  read: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: number) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: number) => void;
  getLocalizedNotification: (notification: Notification) => {
    title: string;
    message: string;
    time: string;
  };
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Bilingual mock notifications
const initialNotifications: Notification[] = [
  {
    id: 1,
    type: 'warning',
    title: 'Overdue Bill',
    titlePtBR: 'Fatura Vencida',
    message: 'Invoice #INV-001 from Acme Supplies is 5 days overdue. Total amount: $4,500.00',
    messagePtBR: 'NF-e #NF-001 de Fornecedor ABC est\u00e1 vencida h\u00e1 5 dias. Valor total: R$ 22.500,00',
    time: '2 hours ago',
    timePtBR: 'H\u00e1 2 horas',
    read: false,
  },
  {
    id: 2,
    type: 'success',
    title: 'Payment Received',
    titlePtBR: 'Pagamento Recebido',
    message: 'Global Tech Industries paid $25,000.00 for Invoice #INV-005',
    messagePtBR: 'Empresa Global Tech pagou R$ 125.000,00 pela NF-e #NF-005',
    time: '5 hours ago',
    timePtBR: 'H\u00e1 5 horas',
    read: false,
  },
  {
    id: 3,
    type: 'info',
    title: 'AI Processing Complete',
    titlePtBR: 'Processamento IA Conclu\u00eddo',
    message: '12 invoices have been auto-categorized. Review the categorizations in Accounts Receivable.',
    messagePtBR: '12 notas fiscais foram categorizadas automaticamente. Revise as categoriza\u00e7\u00f5es em Contas a Receber.',
    time: '1 day ago',
    timePtBR: 'H\u00e1 1 dia',
    read: false,
  },
  {
    id: 4,
    type: 'warning',
    title: 'Payment Due Soon',
    titlePtBR: 'Pagamento Pr\u00f3ximo',
    message: 'Bill #BILL-003 to Tech Solutions LLC is due in 3 days. Amount: $12,750.00',
    messagePtBR: 'Fatura #CP-003 para Tech Solutions LTDA vence em 3 dias. Valor: R$ 63.750,00',
    time: '1 day ago',
    timePtBR: 'H\u00e1 1 dia',
    read: true,
  },
  {
    id: 5,
    type: 'success',
    title: 'New Customer Added',
    titlePtBR: 'Novo Cliente Adicionado',
    message: 'Pacific Retail Group has been added to your customers.',
    messagePtBR: 'Grupo Varejo Pac\u00edfico foi adicionado aos seus clientes.',
    time: '2 days ago',
    timePtBR: 'H\u00e1 2 dias',
    read: true,
  },
  {
    id: 6,
    type: 'info',
    title: 'Weekly Report Available',
    titlePtBR: 'Relat\u00f3rio Semanal Dispon\u00edvel',
    message: 'Your weekly financial summary for Jan 12-19, 2026 is ready to view.',
    messagePtBR: 'Seu resumo financeiro semanal de 12-19 Jan 2026 est\u00e1 pronto para visualiza\u00e7\u00e3o.',
    time: '3 days ago',
    timePtBR: 'H\u00e1 3 dias',
    read: true,
  },
  {
    id: 7,
    type: 'error',
    title: 'Payment Failed',
    titlePtBR: 'Pagamento Falhou',
    message: 'Automatic payment to CloudHost Services failed. Please update your payment method.',
    messagePtBR: 'Pagamento autom\u00e1tico para CloudHost Services falhou. Por favor, atualize seu m\u00e9todo de pagamento.',
    time: '4 days ago',
    timePtBR: 'H\u00e1 4 dias',
    read: true,
  },
  {
    id: 8,
    type: 'success',
    title: 'Integration Connected',
    titlePtBR: 'Integra\u00e7\u00e3o Conectada',
    message: 'QuickBooks integration has been successfully connected to your account.',
    messagePtBR: 'Integra\u00e7\u00e3o com ContaAzul foi conectada com sucesso \u00e0 sua conta.',
    time: '5 days ago',
    timePtBR: 'H\u00e1 5 dias',
    read: true,
  },
];

export function NotificationProvider({ children }: { children: ReactNode }): JSX.Element {
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
  const { isBrazil } = useMarket();

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const markAsRead = useCallback((id: number): void => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback((): void => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const deleteNotification = useCallback((id: number): void => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const getLocalizedNotification = useCallback(
    (notification: Notification) => ({
      title: isBrazil && notification.titlePtBR ? notification.titlePtBR : notification.title,
      message: isBrazil && notification.messagePtBR ? notification.messagePtBR : notification.message,
      time: isBrazil && notification.timePtBR ? notification.timePtBR : notification.time,
    }),
    [isBrazil]
  );

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      getLocalizedNotification,
    }),
    [notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, getLocalizedNotification]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
